import { Injectable } from '@angular/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { GoogleTagManagerEvent } from '../../models/google-tag-manager-event/google-tag-manager-event.interface';
import { GOOGLE_ANALYTICS4_EVENTS } from '../../constants/google-analytics4-events.constants';
import { UserTypeService, constants } from '@edassist/common';
import { AuthenticationService, ParticipantService } from '@edassist/api';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class GoogleTagManagerEventService {
	// Inject the GTM service
	constructor(
		private readonly gtmService: GoogleTagManagerService,
		private readonly authenticationService: AuthenticationService,
		private readonly participantService: ParticipantService
	) {}

	sendPageView(
		userId: string,
		edAssistId: string,
		bhClientName: string,
		employeeZip: string,
		bhClientId: string
	): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const pageView: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.PAGE_VIEW,
			user_id: userId,
			edassist_id: edAssistId,
			bh_client_name: bhClientName,
			employee_zip: employeeZip,
			bh_client_id: bhClientId
		};
		// Send the page_view event to GTM
		this.gtmService.pushTag(pageView);
	}

	sendLogin(
		benefitId: string,
		benefitSource: string,
		userId: string,
		edAssistId: string,
		bhClientName: string,
		employeeZip: string,
		bhClientId: string
	): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const login: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.LOGIN,
			benefit_id: benefitId,
			benefit_source: benefitSource,
			user_id: userId,
			edassist_id: edAssistId,
			bh_client_name: bhClientName,
			employee_zip: employeeZip,
			bh_client_id: bhClientId
		};
		// Send the login event to GTM
		this.gtmService.pushTag(login);
	}

	sendLoginInitiate(benefitId: string, benefitSource: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const loginInitiate: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.LOGIN_INITIATE,
			benefit_id: benefitId,
			benefit_source: benefitSource
		};
		// Send the login_initiate event to GTM
		this.gtmService.pushTag(loginInitiate);
	}
	sendLoginError(benefitId: string, benefitSource: string, errorMessage: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const loginError: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.LOGIN_ERROR,
			benefit_id: benefitId,
			benefit_source: benefitSource,
			error_message: errorMessage
		};
		// Send the login_initiate event to GTM
		this.gtmService.pushTag(loginError);
	}

	sendServiceRegister(serviceCategory: string, serviceName: string, serviceId: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const serviceRegister: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.SERVICE_REGISTER,
			service_category: serviceCategory,
			service_name: serviceName,
			service_id: serviceId
		};
		// Send the service_register event to GTM
		this.gtmService.pushTag(serviceRegister);
	}

	sendServiceEnroll(
		serviceCategory: string,
		serviceName: string,
		serviceId: string,
		subServiceName: string,
		subServiceId: string
	): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const serviceEnroll: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.SERVICE_ENROLL,
			service_category: serviceCategory,
			service_name: serviceName,
			service_id: serviceId,
			sub_service_name: subServiceName,
			sub_service_id: subServiceId
		};
		this.participantService
			.isEnrolledForYear(this.authenticationService.currentUserValue.participantId)
			.subscribe((isEnrolled) => {
				serviceEnroll.participant_type =
					isEnrolled ?
						constants.googleAnalytics.participantType.returningParticipant
					:	constants.googleAnalytics.participantType.newParticipant;
				// Send the service_enroll event to GTM
				this.gtmService.pushTag(serviceEnroll);
			});
	}

	sendLogout(benefitId: string, benefitSource: string, userId: string, edassistId: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const logout: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.LOGOUT,
			benefit_id: benefitId,
			benefit_source: benefitSource,
			user_id: userId,
			edassist_id: edassistId
		};
		// Send the logout event to GTM
		this.gtmService.pushTag(logout);
	}

	sendNavigationInteract(linkText: string, linkUrl: string, linkSection: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const navigationInteract: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.NAVIGATION_INTERACT,
			link_text: linkText,
			link_url: linkUrl,
			link_section: linkSection
		};
		// Send the navigation_interact event to GTM
		this.gtmService.pushTag(navigationInteract);
	}

	sendCtaClick(linkText: string, linkUrl: string, linkSection: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const ctaClick: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.CTA_CLICK,
			link_text: linkText,
			link_url: linkUrl,
			link_section: linkSection
		};
		// Send the cta_click event to GTM
		this.gtmService.pushTag(ctaClick);
	}

	sendFormStart(formName: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const formStart: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.FORM_START,
			form_name: formName
		};
		// Send the form_start event to GTM
		this.gtmService.pushTag(formStart);
	}

	sendFormFill(formName: string, fieldName: string, autoFilled: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const formFill: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.FORM_FILL,
			form_name: formName,
			field_name: fieldName,
			autofilled: autoFilled
		};
		// Send the form_fill event to GTM
		this.gtmService.pushTag(formFill);
	}

	sendFormError(formName: string, errorMessage: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const formError: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.FORM_ERROR,
			form_name: formName,
			error_message: errorMessage
		};
		// Send the form_error event to GTM
		this.gtmService.pushTag(formError);
	}

	sendFormSubmitSuccess(formName: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const formSubmitSuccess: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.FORM_SUBMIT_SUCCESS,
			form_name: formName
		};
		// Send the form_submit_success event to GTM
		this.gtmService.pushTag(formSubmitSuccess);
	}

	sendOutboundLinkClick(linkText: string, linkUrl: string, linkSection: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const outboundLinkClick: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.OUTBOUND_LINK_CLICK,
			link_text: linkText,
			link_url: linkUrl,
			link_section: linkSection
		};
		// Send the outbound_link_click event to GTM
		this.gtmService.pushTag(outboundLinkClick);
	}

	sendAccordionClick(interactionType: string, accordionName: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const accordionClick: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.ACCORDION_CLICK,
			interaction_type: interactionType,
			accordion_name: accordionName
		};
		// Send the accordion_click event to GTM
		this.gtmService.pushTag(accordionClick);
	}

	sendLiveChatInteract(interactionType: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}

		this.participantService
			.isEnrolledForYear(this.authenticationService.currentUserValue.participantId)
			.subscribe((isEnrolled) => {
				const liveChatInteract: GoogleTagManagerEvent = {
					event: GOOGLE_ANALYTICS4_EVENTS.LIVE_CHAT_INTERACT,
					interaction_type: interactionType,
					participant_type:
						isEnrolled ?
							constants.googleAnalytics.participantType.returningParticipant
						:	constants.googleAnalytics.participantType.newParticipant
				};

				// Send the live_chat_interact event to GTM
				this.gtmService.pushTag(liveChatInteract);
			});
	}

	sendLinkClickContact(linkText: string, linkUrl: string, linkSection: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const linkClickContact: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.LINK_CLICK_CONTACT,
			link_text: linkText,
			link_url: linkUrl,
			link_section: linkSection
		};
		// Send the link_click_contact event to GTM
		this.gtmService.pushTag(linkClickContact);
	}

	sendVideoStart(videoProvider: string, videoTitle: string, videoUrl: string, videoDuration: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const videoStart: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.VIDEO_START,
			video_provider: videoProvider,
			video_title: videoTitle,
			video_url: videoUrl,
			video_duration: videoDuration
		};
		// Send the video_start event to GTM
		this.gtmService.pushTag(videoStart);
	}

	sendVideoProgress(
		videoPercent: string,
		videoCurrentTime: string,
		videoProvider: string,
		videoTitle: string,
		videoUrl: string,
		videoDuration: string
	): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const videoProgress: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.VIDEO_PROGRESS,
			video_percent: videoPercent,
			video_current_time: videoCurrentTime,
			video_provider: videoProvider,
			video_title: videoTitle,
			video_url: videoUrl,
			video_duration: videoDuration
		};
		// Send the video_progress event to GTM
		this.gtmService.pushTag(videoProgress);
	}

	sendVideoComplete(
		videoProvider: string,
		videoCurrentTime: string,
		videoTitle: string,
		videoUrl: string,
		videoDuration: string
	): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const videoComplete: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.VIDEO_COMPLETE,
			video_provider: videoProvider,
			video_current_time: videoCurrentTime,
			video_title: videoTitle,
			video_url: videoUrl,
			video_duration: videoDuration
		};
		// Send the video_complete event to GTM
		this.gtmService.pushTag(videoComplete);
	}

	sendContentInteract(contentId: string, contentDate: string, contentType: string, contentTitle: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const contentInteract: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.CONTENT_INTERACT,
			content_id: contentId,
			content_date: contentDate,
			content_type: contentType,
			content_title: contentTitle
		};
		// Send the content_interact event to GTM
		this.gtmService.pushTag(contentInteract);
	}

	sendPasswordReset(benefitId: string, benefitSource: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const passwordReset: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.PASSWORD_RESET,
			benefit_id: benefitId,
			benefit_source: benefitSource
		};
		// Send the password_reset event to GTM
		this.gtmService.pushTag(passwordReset);
	}

	sendPasswordResetSuccess(benefitId: string, benefitSource: string): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(this.authenticationService.currentUserValue.userTypeId)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const passwordResetSuccess: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.PASSWORD_RESET_SUCCESS,
			benefit_id: benefitId,
			benefit_source: benefitSource
		};
		// Send the password_reset_success event to GTM
		this.gtmService.pushTag(passwordResetSuccess);
	}

	sendPathwayInteract(
		linkText: string,
		pathwayName: string,
		globalJobTitle?: string,
		clientSpecificJobTitle?: string
	): void {
		// Check if the user is an internal admin
		if (
			this.authenticationService.currentUserValue &&
			UserTypeService.isInternalAdmin(
				this.authenticationService.currentUserValue.userTypeId
			)
		) {
			// User is an internal admin, return early without sending the event
			return;
		}
		const pathwayInteract: GoogleTagManagerEvent = {
			event: GOOGLE_ANALYTICS4_EVENTS.PATHWAY_INTERACT,
			link_text: linkText,
			pathway_name: pathwayName,
			global_job_title: globalJobTitle,
			client_specific_job_title: clientSpecificJobTitle
		};
		// Send the pathway_interact event to GTM
		this.gtmService.pushTag(pathwayInteract);
	}
}
